// @ts-nocheck
import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StarsRoundedIcon from '@mui/icons-material/StarsRounded';

const MobilePricingTable = ({ plans, features, onChoose, referralDiscount }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const resolvedPlans = plans;
  const resolvedFeatures = useMemo(() => {
    if (features) return features;
    return Array.from(new Set(
      resolvedPlans.flatMap((p) => Array.isArray(p.features) ? p.features : Object.keys(p.features))
    ));
  }, [features, resolvedPlans]);

  const calculateDiscountedPrice = (originalPrice, discountType, discountAmount) => {
    if (!discountType || !discountAmount || originalPrice === 'Free' || originalPrice === 'Custom') {
      return originalPrice;
    }

    const priceMatch = originalPrice.match(/A\$(\d+(?:\.\d+)?)/);
    if (!priceMatch) return originalPrice;

    const numericPrice = parseFloat(priceMatch[1]);
    let discountedPrice;

    if (discountType === 'PERCENT') {
      discountedPrice = numericPrice * (1 - discountAmount / 100);
    } else if (discountType === 'FIXED') {
      discountedPrice = Math.max(0, numericPrice - discountAmount);
    } else {
      return originalPrice;
    }

    return `A$${discountedPrice.toFixed(2)}`;
  };

  const getDisplayPrice = (plan) => {
    if (!referralDiscount?.isValid || !referralDiscount?.discountType || !referralDiscount?.discountAmount) {
      return plan.price;
    }

    const discountedPrice = calculateDiscountedPrice(
      plan.price,
      referralDiscount.discountType,
      referralDiscount.discountAmount
    );

    return {
      original: plan.price,
      discounted: discountedPrice,
      hasDiscount: discountedPrice !== plan.price,
    };
  };

  const renderPrice = (plan) => {
    const displayPrice = getDisplayPrice(plan);

    if (typeof displayPrice === 'string') {
      return (
        <>
          {displayPrice}{' '}
          <Typography component="span" variant="body1" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, color: '#64748b' }}>
            {plan.period}
          </Typography>
        </>
      );
    }

    if (displayPrice.hasDiscount) {
      return (
        <Box>
          <Typography component="span" sx={{ textDecoration: 'line-through', color: 'text.secondary', fontSize: { xs: '1.15rem', sm: '1.35rem' } }}>
            {displayPrice.original}
          </Typography>
          <Typography component="span" sx={{ color: 'success.main', fontWeight: 700 }}>
            {' '}{displayPrice.discounted}{' '}
          </Typography>
          <Typography component="span" variant="body1" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, color: '#64748b' }}>
            {plan.period}
          </Typography>
        </Box>
      );
    }

    return (
      <>
        {displayPrice.original}{' '}
        <Typography component="span" variant="body1" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, color: '#64748b' }}>
          {plan.period}
        </Typography>
      </>
    );
  };

  const renderPlan = (plan, idx) => (
    <Card
      key={idx}
      sx={{
        borderRadius: 6,
        height: '100%',
        position: 'relative',
        overflow: 'visible',
        background: plan.isRecommended
          ? 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(245,247,255,1) 100%)'
          : '#ffffff',
        border: plan.isRecommended ? '1px solid rgba(99, 102, 241, 0.38)' : '1px solid rgba(148, 163, 184, 0.18)',
        boxShadow: plan.isRecommended
          ? '0 28px 70px rgba(79, 70, 229, 0.18)'
          : '0 24px 60px rgba(15, 23, 42, 0.08)',
        transform: plan.isRecommended && isDesktop ? 'scale(1.02)' : 'none',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
        '&:hover': {
          transform: isDesktop ? (plan.isRecommended ? 'scale(1.03) translateY(-6px)' : 'translateY(-6px)') : 'translateY(-4px)',
          boxShadow: plan.isRecommended
            ? '0 34px 80px rgba(79, 70, 229, 0.24)'
            : '0 30px 70px rgba(15, 23, 42, 0.12)',
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, sm: 3.2 } }}>
        <Box sx={{ minHeight: 28, mb: 1.1 }}>
          {plan.isRecommended && (
            <Chip
              icon={<StarsRoundedIcon sx={{ color: '#ffffff !important' }} />}
              label="Most Popular"
              sx={{ background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', color: '#ffffff', fontWeight: 700 }}
            />
          )}
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', sm: '1.3rem' }, color: '#0f172a' }}>
          {plan.title}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, mt: 1, mb: 0.6, fontSize: { xs: '2rem', sm: '2.35rem' }, color: '#0f172a' }}>
          {renderPrice(plan)}
        </Typography>
        <Typography sx={{ color: '#64748b', mb: 2.6, minHeight: 24 }}>
          {plan.isRecommended ? 'Best balance of guidance, analytics, and momentum.' : 'Clear features with the same existing plan behavior.'}
        </Typography>

        <Box sx={{ mt: 2, mb: { xs: 3.5, sm: 4.5 }, borderTop: '1px solid rgba(226,232,240,0.85)', pt: 2 }}>
          {resolvedFeatures.map((feature, fid) => {
            const isArray = Array.isArray(plan.features);
            let display;
            if (isArray) {
              const included = plan.features.includes(feature);
              display = included ? (
                <CheckCircleOutlineIcon color="success" fontSize="small" />
              ) : (
                <HighlightOffIcon color="error" fontSize="small" />
              );
            } else {
              const value = plan.features[feature] ?? 'X';
              if (value === 'X' || value === 'No') {
                display = <HighlightOffIcon color="error" fontSize="small" />;
              } else if (value === 'Yes') {
                display = <CheckCircleOutlineIcon color="success" fontSize="small" />;
              } else {
                display = <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, fontWeight: 600 }}>{value}</Typography>;
              }
            }

            return (
              <Box
                key={fid}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 1.2,
                  py: { xs: 0.6, sm: 0.7 },
                  borderBottom: fid !== resolvedFeatures.length - 1 ? '1px solid rgba(241,245,249,0.9)' : 'none',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    lineHeight: 1.5,
                    flex: 1,
                    pr: 1,
                    color: '#334155',
                  }}
                >
                  {feature}
                  {feature === 'Upload Worksheets,tailored feedback by Teacher' && (
                    <Tooltip title="Tailored feedback with correction by highly experienced teacher" arrow>
                      <InfoOutlinedIcon fontSize="inherit" color="info" sx={{ cursor: 'pointer', flexShrink: 0 }} />
                    </Tooltip>
                  )}
                </Typography>
                <Box sx={{ flexShrink: 0, pt: 0.15 }}>{display}</Box>
              </Box>
            );
          })}
        </Box>

        <Button
          fullWidth
          variant={plan.isRecommended ? 'contained' : 'outlined'}
          sx={{
            mt: 1,
            py: { xs: 1.15, sm: 1.35 },
            fontSize: { xs: '0.92rem', sm: '1rem' },
            fontWeight: 700,
            borderRadius: 999,
            background: plan.isRecommended ? 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)' : undefined,
            borderColor: 'rgba(99,102,241,0.24)',
            color: plan.isRecommended ? '#ffffff' : '#1e3a8a',
            boxShadow: plan.isRecommended ? '0 18px 40px rgba(79, 70, 229, 0.22)' : 'none',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: plan.isRecommended ? '0 22px 48px rgba(79, 70, 229, 0.28)' : '0 10px 24px rgba(15, 23, 42, 0.08)',
              borderColor: 'rgba(99,102,241,0.34)',
              background: plan.isRecommended ? 'linear-gradient(135deg, #1d4ed8 0%, #6d28d9 100%)' : 'rgba(238,242,255,0.6)',
            },
          }}
          onClick={() => onChoose(plan.title)}
        >
          {plan.buttonText}
        </Button>
      </CardContent>
    </Card>
  );

  if (isDesktop) {
    return (
      <Box sx={{ mt: 2, mb: 4 }}>
        <Grid container spacing={4} alignItems="stretch">
          {resolvedPlans.map((plan, idx) => (
            <Grid item md={4} key={idx}>
              {renderPlan(plan, idx)}
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, sm: 4 }, mb: 4, px: { xs: 1, sm: 0 } }}>
      {resolvedPlans.map(renderPlan)}
    </Box>
  );
};

export default MobilePricingTable;
